import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePropertyDepartmentsQuery } from '@/hooks/queries/usePropertyDashboard';
import type { Department } from '@/hooks/queries/usePropertyDashboard';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DataTable,
  TableColumn,
  PaginationInfo,
} from '@/components/shared/data-table';
import { cn } from '@/lib/utils';

interface DepartmentsTabProps {
  propertyId?: string;
}

export function DepartmentsTab({ propertyId }: DepartmentsTabProps) {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const itemsPerPage = 10;

  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Use React Query hook
  const { data: response, isLoading } = usePropertyDepartmentsQuery(
    propertyId || '',
    {
      page: currentPage,
      pageSize: itemsPerPage,
      search: searchQuery,
      dateFrom: dateFilter || undefined,
    }
  );

  const departments = response?.data || [];
  const pagination = response?.pagination;

  const handlePageChange = (page: number) => {
    if (pagination) {
      setCurrentPage(Math.max(1, Math.min(page, pagination.last_page)));
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Define table columns
  const columns: TableColumn<Department>[] = [
    {
      key: 'name',
      label: t('properties.details.departmentsTab.department'),
      className: 'font-medium',
    },
    {
      key: 'last_score',
      label: t('properties.details.departmentsTab.lastScore'),
      render: dept => (
        <div
          className={cn(
            'flex items-center space-x-2',
            isRTL && 'flex-row-reverse space-x-reverse'
          )}
        >
          <span className={`font-medium ${getScoreColor(dept.last_score)}`}>
            {dept.last_score}%
          </span>
          <div className="w-16 bg-muted rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${dept.last_score}%` }}
            />
          </div>
        </div>
      ),
    },
    {
      key: 'touchpoints',
      label: t('properties.details.departmentsTab.touchpoints'),
    },
    {
      key: 'total_standards',
      label: t('properties.details.departmentsTab.standards'),
    },
    {
      key: 'completed_assessments',
      label: t('properties.details.departmentsTab.completed'),
      render: dept => (
        <span className="text-green-600 font-medium">
          {dept.completed_assessments}
        </span>
      ),
    },
    {
      key: 'pending_assessments',
      label: t('properties.details.departmentsTab.pending'),
      render: dept => (
        <span
          className={
            dept.pending_assessments > 0
              ? 'text-orange-600 font-medium'
              : 'text-muted-foreground'
          }
        >
          {dept.pending_assessments}
        </span>
      ),
    },
    {
      key: 'last_assessment',
      label: t('properties.details.departmentsTab.lastAssessmentDate'),
      render: dept =>
        dept.last_assessment
          ? new Date(dept.last_assessment).toLocaleDateString()
          : t('properties.details.departmentsTab.noAssessment', 'N/A'),
    },
  ];

  // Pagination info
  const paginationInfo: PaginationInfo = {
    current: pagination
      ? typeof pagination.current_page === 'string'
        ? parseInt(pagination.current_page)
        : pagination.current_page
      : 1,
    total: pagination?.last_page || 1,
    count: pagination?.total || 0,
    itemsPerPage: pagination
      ? typeof pagination.per_page === 'string'
        ? parseInt(pagination.per_page)
        : pagination.per_page
      : itemsPerPage,
  };

  return (
    <div className="space-y-4">
      {/* Departments Table */}
      <Card>
        <CardHeader>
          <div
            className={cn(
              'flex items-center justify-between',
              isRTL && 'flex-row-reverse'
            )}
          >
            <div className={cn(isRTL && 'text-right')}>
              <CardTitle>
                {t('properties.details.departmentsTab.propertyDepartments')}
              </CardTitle>
              <CardDescription>
                {t('properties.details.departmentsTab.departmentsOverview')}
              </CardDescription>
            </div>
            <div
              className={cn(
                'text-sm text-muted-foreground',
                isRTL && 'text-right'
              )}
            >
              {isLoading ? t('loading') : null}
            </div>
          </div>
        </CardHeader>

        {/* Filters Section */}
        <div className="px-6 py-4 border-b">
          <div
            className={cn(
              'flex flex-col sm:flex-row gap-4',
              isRTL && 'sm:flex-row-reverse'
            )}
          >
            {/* Search Input */}
            <div className="flex-1 min-w-[200px]">
              <Label htmlFor="department-search" className="sr-only">
                {t('properties.details.departmentsTab.searchDepartments')}
              </Label>
              <Input
                id="department-search"
                placeholder={t(
                  'properties.details.departmentsTab.searchDepartments'
                )}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className={cn('w-full', isRTL && 'text-right')}
              />
            </div>

            {/* Date Filter */}
            <div className="min-w-[160px]">
              <Label htmlFor="date-filter" className="sr-only">
                {t('properties.details.departmentsTab.filterByDate')}
              </Label>
              <Input
                id="date-filter"
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                className={cn('w-full', isRTL && 'text-right')}
              />
            </div>

            {/* Clear Filters Button */}
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setDateFilter('');
              }}
              className="shrink-0"
            >
              {t('properties.details.departmentsTab.clearFilters')}
            </Button>
          </div>

          {/* Filter Results Info */}
          <div
            className={cn(
              'flex items-center justify-between mt-3 pt-3 border-t text-sm text-muted-foreground',
              isRTL && 'flex-row-reverse'
            )}
          >
            <div className={cn(isRTL && 'text-right')}>
              {!isLoading && pagination && (searchQuery || dateFilter) && (
                <span>
                  {t('properties.details.departmentsTab.filteredBySearch')}
                </span>
              )}
            </div>
          </div>
        </div>

        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('loading')}</div>
            </div>
          ) : (
            <DataTable
              data={departments}
              columns={columns}
              pagination={paginationInfo}
              onPageChange={handlePageChange}
              emptyMessage={t(
                'properties.details.departmentsTab.noDepartments'
              )}
              tableCaption={t(
                'properties.details.departmentsTab.propertyDepartments'
              )}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
